import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import PermissionService from '@/utils/PermissionService';
import { Roles } from '@/models/Roles';
import { ACCESS_LEVEL } from '@/models/user/ACCESS_LEVEL';
import { Scale } from '@/services/canvas/Scale';
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import { useMapArea } from '@/modules/map-editor';
import type { Node, NodeSelectionPoint } from '@/modules/map-editor';
import type {
  CollaborativePointerState,
  NodeDesignData,
  DashboardOption
} from '@/modules/map-editor';
import type { MapAreaState } from '@/modules/map-editor';

/**
 * Composable for managing pointer-related state.
 * Bridges Vuex getters/actions to the pointer system — provides reactive computed refs
 * and thin action dispatchers consumed by usePointerEvents.
 */
export function usePointerStateManagement() {
  const store = useStore();
  const { transferMapArea } = useMapArea();

  const nodeSizerPointHovered = ref<NodeSelectionPoint | null>(null);
  const isNodeSizing = ref(false);
  const nodeDimensions = ref<NodeDesignData>({});

  // Vuex getters
  const isUserAccessLevelRead = computed(() =>
    store.getters["user/getUserAccessLevel"] === ACCESS_LEVEL.READ
  );

  const hasUserPermission = computed(() => {
    const userRole = store.getters["user/getCurrentUserRole"];
    return PermissionService.permissionCheck({
      userRole,
      permission: Roles.Student
    });
  });

  let pointerTimer: Date | null = null;
  const pointerFrequencyAtSecond = 8; // once per 125ms

  const sendPointerStateWithThrottle = (pointerState: CollaborativePointerState): void => {
    const now = new Date();
    if (!pointerTimer || (now.getTime() - pointerTimer.getTime()) / (1000 / pointerFrequencyAtSecond) > 1) {
      store.dispatch('simultaneousEditing/sendPointerState', pointerState);
      pointerTimer = now;
    }
  };

  const nodes = computed(() => store.getters["nodes/getNodes"]);
  const edges = computed(() => store.getters["edges/getEdges"]);
  const selectedNode = computed(() => store.getters['nodes/getSelectedNode']);
  const selectedEdge = computed(() => store.getters['edges/getSelectedEdge']);
  const currentLayerId = computed(() => store.getters["layers/getCurrentLayerId"]);
  const isMapTransfer = computed(() => store.getters['maps/isMapTransfer']);
  const isNodeDrawingMode = computed(() => store.getters["dashboardOptions/isNodeDrawingMode"]);
  const isEdgeConnectionsDrawingMode = computed(() => store.getters["dashboardOptions/isEdgeConnectionsDrawingMode"]);
  const isQuickEditBarContentTracked = computed(() => store.getters["nodes/content/getQuickEditBarContentTrackedChanges"]);
  const edgeConnections = computed(() => store.getters['edges/getEdgeConnections']);
  const userCurrent = computed(() => store.getters["simultaneousEditing/getCurrentUser"]);
  const selectedCount = computed(() => store.getters['nodes/getSelectedCount']);
  const selectedNodes = computed<Node[]>(() => {
    const selectedMap = store.getters['nodes/getSelectedNodes'] as Map<number, Node> | undefined;
    // Access size via getSelectedCount to ensure Vue tracks Map mutations reactively
    const size = store.getters['nodes/getSelectedCount'] as number;
    if (!selectedMap || size === 0) return [];
    return Array.from(selectedMap.values());
  });
  const isMultiSelection = computed(() => store.getters['nodes/isMultiSelection']);

  const editedNode = computed(() => store.getters["simultaneousEditing/getEditedNode"]);
  const editedEdge = computed(() => store.getters["simultaneousEditing/getEditedEdge"]);
  const isNodeDesignSettingsOpened = computed(() =>
    store.getters["dashboardOptions/getOptionActive"]?.VALUE === DASHBOARD_OPTIONS.CREATE.GROUPS.NODE.NODE_DESIGN.VALUE
  );
  const isEdgeDesignSettingsOpened = computed(() =>
    store.getters["dashboardOptions/getOptionActive"]?.VALUE === DASHBOARD_OPTIONS.CREATE.GROUPS.EDGE.EDGE_DESIGN.VALUE
  );
  const sectionActive = computed(() => store.getters["dashboardOptions/getSectionActive"]);

  // Action dispatchers (callbacks)
  const saveNodePosition = async (nodeId: number | null | 'save'): Promise<void> => {
    await store.dispatch("nodes/position/nodeSavePosition", nodeId);
  };

  const setSelectedNode = async (nodeId: number | null): Promise<void> => {
    await store.dispatch('nodes/setSelectedNode', nodeId);
  };

  const setSelectedEdge = async (edgeId: number | null): Promise<void> => {
    await store.dispatch('edges/setSelectedEdge', edgeId);
  };

  const toggleNodeOptions = async (status: boolean): Promise<void> => {
    await store.dispatch("dashboardOptions/groupNodeToggle", status);
  };

  const toggleEdgeOptions = async (status: boolean): Promise<void> => {
    await store.dispatch("dashboardOptions/groupEdgeToggle", status);
  };

  const updateQuickEditBarContentWarning = async (status: boolean): Promise<void> => {
    await store.dispatch('nodes/content/updateQuickEditBarContentWarning', status);
  };

  const createNode = async (
    event: MouseEvent | TouchEvent,
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    mapArea: MapAreaState
  ): Promise<Node | null> => {
    const pixelRatio = window.devicePixelRatio;
    const touch = 'clientX' in event ? null : (event.changedTouches[0] ?? event.touches[0]);
    // Convert viewport coordinates to canvas-relative ones — the canvas is offset
    // when side panels (e.g. Settings Sidebar) are open, mirroring getPointerPosition().
    const canvasBounds = ctx.canvas.getBoundingClientRect();
    // The narrowing on `touch` above doesn't carry to `event`, so pick a single
    // coordinate source (Touch or MouseEvent) — both expose clientX/clientY.
    const point = touch ?? (event as MouseEvent);
    const clientX = (point.clientX - canvasBounds.left) * pixelRatio;
    const clientY = (point.clientY - canvasBounds.top) * pixelRatio;
    const geometry = {
      x: Number((Scale.xInv(clientX, ctx.canvas.width / 2, zoomLevel) - mapArea.x).toFixed(1)),
      y: Number((Scale.yInv(clientY, ctx.canvas.height / 2, zoomLevel) - mapArea.y).toFixed(1))
    };
    await store.dispatch('maps/mapAwaitResponse', true);
    const nodeOriginalCreated = await store.dispatch('nodes/createNodeRequest', geometry);
    let nodeCreated: Node | null = null;
    if(nodeOriginalCreated) {
      nodeCreated = await store.dispatch('nodes/createNode', nodeOriginalCreated);
    }
    await store.dispatch('maps/mapAwaitResponse', false);
    return nodeCreated;
  };

  const updateNodeDesign = async (designData: NodeDesignData): Promise<void> => {
    if (selectedNode.value) {
      await store.dispatch('nodes/design/nodeDesignUpdate', {
        id: selectedNode.value.id,
        designData
      });
    }
  };

  const saveNodeDesign = async (designData: NodeDesignData): Promise<void> => {
    if (selectedNode.value) {
      await store.dispatch('nodes/design/nodeDesignSave', {
        id: selectedNode.value.id,
        designData
      });
    }
  };

  const connectEdge = async (nodeId: number): Promise<void> => {
    await store.dispatch('edges/connectEdge', nodeId);
  };

  const createEdge = async (): Promise<void> => {
    await store.dispatch('maps/mapAwaitResponse', true);
    await store.dispatch('edges/create/edgeCreateRequest');
    await store.dispatch('edges/cancelEdgeConnection');
    await store.dispatch('maps/mapAwaitResponse', false);
  };

  const markNodeConnect = async (nodeId: number): Promise<void> => {
    await store.dispatch('nodes/markNodeConnect', nodeId);
  };

  const stopEditingNode = async (): Promise<void> => {
    await store.dispatch('simultaneousEditing/stopEditingNode', null);
  };

  const stopEditingEdge = async (): Promise<void> => {
    await store.dispatch('simultaneousEditing/stopEditingEdge', null);
  };

  const chooseOption = async (option: DashboardOption | null): Promise<void> => {
    await store.dispatch('dashboardOptions/optionChoose', option);
  };

  const toggleSection = async (section: string): Promise<void> => {
    await store.dispatch("dashboardOptions/sectionToggle", section);
  };

  const closeSidebar = async (): Promise<void> => {
    await store.dispatch('dashboardOptions/sidebarClose');
  };

  const selectNode = async (node: Node): Promise<void> => {
    await store.dispatch('nodes/selectNode', node);
  };

  const deselectAllNodes = async (): Promise<void> => {
    await store.dispatch('nodes/deselectAllNodes');
  };

  const selectMultipleNodes = async (nodes: Node[]): Promise<void> => {
    await store.dispatch('nodes/selectMultipleNodes', nodes);
  };

  const cancelEdgeConnection = async (): Promise<void> => {
    await store.dispatch('edges/cancelEdgeConnection');
  };

  // Scrolls the map so the Quick-Edit-Bar attached to `selectedNode` stays fully visible
  const adjustMapForQuickEditBar = async (
    selectedNode: Node,
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    mapArea: MapAreaState
  ): Promise<void> => {
    const pixelRatio = window.devicePixelRatio;
    const QEB_HEIGHT = 77.25 * pixelRatio;
    const QEB_WIDTH = 500 * pixelRatio;
    const QEB_PADDING_TOP = 23 * pixelRatio;
    const QEB_PADDING_SIDE = 60 * pixelRatio;

    const windowWidth = window.innerWidth * pixelRatio;
    const windowHeight = window.innerHeight * pixelRatio;

    const xOffsetRight = Scale.x(selectedNode.x + mapArea.x, ctx.canvas.width / 2, zoomLevel) + QEB_WIDTH / 2;
    const xOffsetLeft = Scale.x(selectedNode.x + mapArea.x, ctx.canvas.width / 2, zoomLevel) - QEB_WIDTH / 2;
    const yOffsetBottom = Scale.y(
      selectedNode.y + selectedNode.height / 2 + (selectedNode.border ? selectedNode.borderWidth : 0) + mapArea.y,
      ctx.canvas.height / 2,
      zoomLevel
    ) + QEB_PADDING_TOP + QEB_HEIGHT;

    const shift = { x: 0, y: 0 };
    let isShifted = false;

    // Check if node is partially visible from left side
    if (xOffsetLeft < 0) {
      shift.x = -parseInt((xOffsetLeft - QEB_PADDING_SIDE).toFixed(0)) / zoomLevel;
      isShifted = true;
    }

    // Check if node is partially visible from right side
    if (xOffsetRight > windowWidth) {
      shift.x = (parseInt((windowWidth - xOffsetRight).toFixed(0)) - QEB_PADDING_SIDE) / zoomLevel;
      isShifted = true;
    }

    // Check if node is partially visible from bottom side
    if (yOffsetBottom > windowHeight) {
      shift.y = (windowHeight - yOffsetBottom - QEB_PADDING_SIDE) / zoomLevel;
      isShifted = true;
    }

    if (isShifted) {
      transferMapArea(shift, {
        onStart: () => store.dispatch('maps/mapTransfer', true),
        onComplete: () => store.dispatch('maps/mapTransfer', false),
        onCancel: () => store.dispatch('maps/mapTransfer', false)
      });
    }
  };

  watch(() => selectedNode.value, (newNode, oldNode) => {
    if (newNode !== oldNode) {
      nodeSizerPointHovered.value = null;
    }
  });

  return {
    // Local state
    nodeSizerPointHovered,
    isNodeSizing,
    nodeDimensions,

    // Computed state — element data
    nodes,
    edges,
    selectedNode,
    selectedEdge,
    currentLayerId,
    isMapTransfer,

    // Computed state — permissions & UI
    isUserAccessLevelRead,
    hasUserPermission,
    isNodeDrawingMode,
    isEdgeConnectionsDrawingMode,
    isQuickEditBarContentTracked,
    edgeConnections,
    userCurrent,
    editedNode,
    editedEdge,
    isNodeDesignSettingsOpened,
    isEdgeDesignSettingsOpened,
    sectionActive,

    // Action callbacks
    saveNodePosition,
    setSelectedNode,
    setSelectedEdge,
    toggleNodeOptions,
    toggleEdgeOptions,
    updateQuickEditBarContentWarning,
    createNode,
    connectEdge,
    createEdge,
    markNodeConnect,
    stopEditingNode,
    stopEditingEdge,
    chooseOption,
    toggleSection,
    closeSidebar,
    cancelEdgeConnection,
    sendPointerStateWithThrottle,
    adjustMapForQuickEditBar,
    updateNodeDesign,
    saveNodeDesign,
    selectNode,
    deselectAllNodes,
    selectMultipleNodes,
    selectedCount,
    selectedNodes,
    isMultiSelection
  };
}
